import { createTRPCContext } from '@trpc/tanstack-react-query'

import { createTRPCClient, httpLink, httpSubscriptionLink, splitLink } from '@trpc/client'
import type { AppRouter } from 'api/src/types'
import SuperJSON from 'superjson'
import { API_URL } from './constants'

export const { TRPCProvider, useTRPC } = createTRPCContext<AppRouter>()

function trpcHeaders(): Record<string, string> {
  const headers: Record<string, string> = {}
  const anonymousId = typeof window !== 'undefined'
    ? localStorage.getItem('anonymousId')
    : null
  if (anonymousId)
    headers['x-anonymous-id'] = anonymousId
  return headers
}

function trpcFetch(url: RequestInfo | URL, options?: RequestInit) {
  return fetch(url, {
    ...options,
    credentials: 'include',
  })
}

export function perspectiveTokenFetch(
  url: RequestInfo | URL,
  options?: RequestInit,
  fetchImpl: typeof fetch = fetch,
): Promise<Response> {
  const headers = new Headers(options?.headers)
  headers.delete('authorization')
  headers.delete('cookie')
  headers.delete('x-anonymous-id')
  return fetchImpl(url, {
    ...options,
    credentials: 'omit',
    headers,
    referrerPolicy: 'no-referrer',
  })
}

const STATUS_TELEMETRY_KEEPALIVE_MAX_BYTES = 64 * 1024

function statusTelemetryHeaders(headers?: HeadersInit): Headers {
  const sanitized = new Headers(headers)
  sanitized.delete('authorization')
  sanitized.delete('cookie')
  sanitized.delete('x-anonymous-id')
  return sanitized
}

function requestBodyByteLength(
  body: BodyInit | null | undefined,
): number | null {
  if (typeof body === 'string')
    return new TextEncoder().encode(body).byteLength
  if (body instanceof URLSearchParams)
    return new TextEncoder().encode(body.toString()).byteLength
  if (body instanceof ArrayBuffer)
    return body.byteLength
  if (ArrayBuffer.isView(body))
    return body.byteLength
  if (typeof Blob !== 'undefined' && body instanceof Blob)
    return body.size
  return null
}

export function statusTelemetryFetch(
  url: RequestInfo | URL,
  options?: RequestInit,
  fetchImpl: typeof fetch = fetch,
): Promise<Response> {
  const bodyBytes = requestBodyByteLength(options?.body)
  return fetchImpl(url, {
    ...options,
    credentials: 'omit',
    headers: statusTelemetryHeaders(options?.headers),
    keepalive:
      bodyBytes !== null && bodyBytes <= STATUS_TELEMETRY_KEEPALIVE_MAX_BYTES,
    referrerPolicy: 'no-referrer',
  })
}

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    splitLink({
      condition: op => op.type === 'subscription',
      true: httpSubscriptionLink({
        url: `${API_URL}/api`,
        transformer: SuperJSON,
        eventSourceOptions: () => ({ withCredentials: true }),
      }),
      false: httpLink({
        url: `${API_URL}/api`,
        transformer: SuperJSON,
        headers: trpcHeaders,
        fetch: trpcFetch,
      }),
    }),
  ],
})

export const statusTelemetryTrpcClient = createTRPCClient<AppRouter>({
  links: [httpLink({
    url: `${API_URL}/api`,
    transformer: SuperJSON,
    fetch: statusTelemetryFetch,
    methodOverride: 'POST',
  })],
})

export const perspectiveTrpcClient = createTRPCClient<AppRouter>({
  links: [httpLink({
    url: `${API_URL}/api`,
    transformer: SuperJSON,
    fetch: perspectiveTokenFetch,
    methodOverride: 'POST',
  })],
})

export const trpcPostClient = createTRPCClient<AppRouter>({
  links: [httpLink({
    url: `${API_URL}/api`,
    transformer: SuperJSON,
    headers: trpcHeaders,
    fetch: trpcFetch,
    methodOverride: 'POST',
  })],
})
